import { initializeApp } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { initializeFirestore, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import { getConfig } from './runtimeConfig';

const firebaseConfig = {
  apiKey: getConfig('VITE_FIREBASE_API_KEY') || 'YOUR_API_KEY',
  authDomain: getConfig('VITE_FIREBASE_AUTH_DOMAIN') || 'YOUR_PROJECT.firebaseapp.com',
  projectId: getConfig('VITE_FIREBASE_PROJECT_ID') || 'YOUR_PROJECT_ID',
  storageBucket: getConfig('VITE_FIREBASE_STORAGE_BUCKET') || 'YOUR_PROJECT.appspot.com',
  messagingSenderId: getConfig('VITE_FIREBASE_MESSAGING_SENDER_ID') || 'YOUR_SENDER_ID',
  appId: getConfig('VITE_FIREBASE_APP_ID') || 'YOUR_APP_ID',
};

// Pre-flight: if localStorage is already near quota, evict app caches before
// initializing Firestore. The SDK writes its own `firestore_clients_*` keys to
// localStorage for multi-tab coordination — a failure there wedges the
// AsyncQueue with INTERNAL ASSERTION FAILED. Aggregations and level_completions
// are safe to drop because Firestore is the source of truth.
(function preflightLocalStorageHealth() {
  try {
    let totalBytes = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const v = localStorage.getItem(k);
      if (v) totalBytes += (k.length + v.length) * 2;
    }
    const totalMB = totalBytes / 1024 / 1024;
    if (totalMB < 4) return;

    const drop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('agg_') || k.startsWith('level_completion_'))) {
        drop.push(k);
      }
    }
    drop.forEach(k => localStorage.removeItem(k));
    console.warn(`⚠️ Pre-flight: localStorage at ${totalMB.toFixed(2)} MB — evicted ${drop.length} cached agg/completion keys`);
  } catch {
    // Best-effort; never block boot.
  }
})();

// Recovery: if the Firestore SDK ever throws INTERNAL ASSERTION FAILED (the
// downstream effect of a QuotaExceededError on its own setItem call), the
// only recovery is to free space and reload — the AsyncQueue is unrecoverable.
let firestoreRecoveryInFlight = false;
window.addEventListener('error', (event) => {
  if (firestoreRecoveryInFlight) return;
  const msg = (event.message || event.error?.message || '') as string;
  const isFirestoreWedge =
    msg.includes('FIRESTORE') && msg.includes('INTERNAL ASSERTION FAILED');
  const isQuotaError =
    msg.includes('QuotaExceededError') ||
    (event.error && event.error.name === 'QuotaExceededError');
  if (!isFirestoreWedge && !isQuotaError) return;

  firestoreRecoveryInFlight = true;
  console.error('🚨 Firestore wedged or quota exceeded — clearing app caches and reloading');
  try {
    const drop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (
        k.startsWith('agg_') ||
        k.startsWith('level_completion_') ||
        k.startsWith('firestore_clients_')
      )) {
        drop.push(k);
      }
    }
    drop.forEach(k => localStorage.removeItem(k));
  } catch {
    // proceed to reload anyway
  }
  window.location.reload();
});

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });

// Enable Firestore offline persistence (dramatically reduces network requests)
// This caches Firestore data locally and serves from cache when available
// Reduces quota usage and improves performance
enableMultiTabIndexedDbPersistence(db).catch((error) => {
  if (error.code === 'failed-precondition') {
    // Multiple tabs open, persistence can only be enabled in one tab at a time
    console.warn('⚠️ Firestore persistence failed: Multiple tabs open');
  } else if (error.code === 'unimplemented') {
    // Browser doesn't support persistence (e.g., Safari private mode)
    console.warn('⚠️ Firestore persistence not supported in this browser');
  } else {
    console.error('❌ Error enabling Firestore persistence:', error);
  }
});

// Configure auth persistence
// browserLocalPersistence stores auth state in localStorage (persists across browser sessions)
// This ensures users stay logged in even after closing the browser
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

export default app;

